import React, { useState } from 'react';
import { Modal, Button, Form, Dropdown, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { createMembership } from '../../../../../../../../crud/clients_api/memberships';
import { FlatMembership } from '../../../../types/membership.types';
import './CreateMembership.scss';

interface CreateMembershipProps {
  show: boolean;
  handleClose: () => void;
  onSuccess: () => void;
  existingMemberships: FlatMembership[];
}

const CreateMembership: React.FC<CreateMembershipProps> = ({
  show,
  handleClose,
  onSuccess,
  existingMemberships,
}) => {
  const [formData, setFormData] = useState({
    membership_name: '',
    membership_description: '',
    discount_percentage: '',
    is_default: false,
    has_next_tier: false,
    next_membership_id: '',
    next_level_required_spent: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const localId = Number(user.local_id) || 0;

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.membership_name.trim()) {
      newErrors.membership_name = 'El nombre es requerido';
    }

    if (!formData.membership_description.trim()) {
      newErrors.membership_description = 'La descripción es requerida';
    }

    const discount = parseFloat(formData.discount_percentage);
    if (isNaN(discount) || discount < 0 || discount > 100) {
      newErrors.discount_percentage = 'El descuento debe estar entre 0 y 100';
    }

    if (formData.has_next_tier) {
      if (!formData.next_membership_id) {
        newErrors.next_membership_id = 'Debes seleccionar la siguiente membresía';
      }
      const requiredSpent = parseFloat(formData.next_level_required_spent);
      if (isNaN(requiredSpent) || requiredSpent <= 0) {
        newErrors.next_level_required_spent = 'El gasto requerido debe ser mayor a 0';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if form can be submitted
  const isFormValid = (): boolean => {
    // Required fields
    if (!formData.membership_name.trim()) return false;
    if (!formData.membership_description.trim()) return false;
    if (!formData.discount_percentage) return false;

    // If has next tier, need to select membership and required spent
    if (formData.has_next_tier) {
      if (!formData.next_membership_id) return false;
      if (!formData.next_level_required_spent) return false;
    }

    return true;
  };

  const getSelectedMembershipName = (): string => {
    if (!formData.next_membership_id) return 'Seleccionar membresía...';
    const selected = existingMemberships.find(
      m => m.membership_id.toString() === formData.next_membership_id
    );
    return selected 
      ? `${selected.membership_name} (${selected.discount_percentage}%)`
      : 'Seleccionar membresía...';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      const createData: any = {
        local_id: localId,
        membership_name: formData.membership_name.trim(),
        membership_description: formData.membership_description.trim() || undefined,
        discount_percentage: parseFloat(formData.discount_percentage),
        is_default: formData.is_default,
      };

      if (formData.has_next_tier) {
        createData.next_membership_id = parseInt(formData.next_membership_id);
        createData.next_level_required_spent = parseFloat(formData.next_level_required_spent);
      }

      await createMembership(createData);
      toast.success('Membresía creada correctamente');
      handleClose();
      onSuccess();
      
      // Reset form
      setFormData({
        membership_name: '',
        membership_description: '',
        discount_percentage: '',
        is_default: false,
        has_next_tier: false,
        next_membership_id: '',
        next_level_required_spent: '',
      });
    } catch (error: any) {
      console.error('Error creating membership:', error);
      const message = error?.message || 'Error al crear la membresía';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered className="create-membership-modal">
      <Modal.Header closeButton>
        <Modal.Title>Crear Membresía</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {/* Nombre */}
          <Form.Group className="mb-3">
            <Form.Label>
              Nombre <span className="required">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.membership_name}
              onChange={(e) =>
                setFormData({ ...formData, membership_name: e.target.value })
              }
              isInvalid={!!errors.membership_name}
              maxLength={100}
              placeholder="Ej: Bronce, Plata, Oro"
            />
            <Form.Control.Feedback type="invalid">
              {errors.membership_name}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Descripción */}
          <Form.Group className="mb-3">
            <Form.Label>
              Descripción <span className="required">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.membership_description}
              onChange={(e) =>
                setFormData({ ...formData, membership_description: e.target.value })
              }
              isInvalid={!!errors.membership_description}
              placeholder="Descripción de la membresía"
            />
            <Form.Control.Feedback type="invalid">
              {errors.membership_description}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Porcentaje de descuento - INLINE */}
          <Form.Group className="mb-3">
            <Form.Label>
              Porcentaje de descuento <span className="required">*</span>
            </Form.Label>
            <div className="percentage-input-wrapper">
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.discount_percentage}
                onChange={(e) =>
                  setFormData({ ...formData, discount_percentage: e.target.value })
                }
                isInvalid={!!errors.discount_percentage}
                placeholder="0 - 100"
                className="percentage-input"
              />
              <span className="percentage-symbol">%</span>
            </div>
            <Form.Control.Feedback type="invalid" className={errors.discount_percentage ? 'd-block' : ''}>
              {errors.discount_percentage}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Membresía por defecto - TOGGLE SWITCH */}
          <div className="toggle-section mb-3">
            <div className="toggle-header">
              <div className="toggle-info">
                <span className="toggle-label">Membresía por defecto</span>
                <span className="toggle-description">Se asignará automáticamente a nuevos clientes</span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={formData.is_default}
                  onChange={(e) =>
                    setFormData({ ...formData, is_default: e.target.checked })
                  }
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>

          {/* Tiene siguiente nivel - TOGGLE SWITCH */}
          <div className="toggle-section mb-3">
            <div className="toggle-header">
              <div className="toggle-info">
                <span className="toggle-label">Tiene siguiente nivel</span>
                <span className="toggle-description">Permite progresión a una membresía superior</span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={formData.has_next_tier}
                  onChange={(e) =>
                    setFormData({ ...formData, has_next_tier: e.target.checked })
                  }
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>

          {/* Siguiente nivel - COLLAPSIBLE CONTENT */}
          {formData.has_next_tier && (
            <div className="next-tier-content mb-3">
              <Form.Group className="mb-3">
                <Form.Label>
                  Siguiente membresía <span className="required">*</span>
                </Form.Label>
                <Dropdown className="custom-dropdown">
                  <Dropdown.Toggle 
                    variant="outline-secondary" 
                    className={`dropdown-toggle-custom ${!!errors.next_membership_id ? 'is-invalid' : ''}`}
                  >
                    {getSelectedMembershipName()}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="dropdown-menu-custom">
                    {existingMemberships.length === 0 ? (
                      <Dropdown.Item disabled>No hay membresías disponibles</Dropdown.Item>
                    ) : (
                      existingMemberships.map((m) => (
                        <Dropdown.Item
                          key={m.membership_id}
                          onClick={() =>
                            setFormData({ 
                              ...formData, 
                              next_membership_id: m.membership_id.toString() 
                            })
                          }
                          active={formData.next_membership_id === m.membership_id.toString()}
                        >
                          {m.membership_name} ({m.discount_percentage}%)
                        </Dropdown.Item>
                      ))
                    )}
                  </Dropdown.Menu>
                </Dropdown>
                {errors.next_membership_id && (
                  <div className="invalid-feedback d-block">
                    {errors.next_membership_id}
                  </div>
                )}
              </Form.Group>

              <Form.Group className="mb-0">
                <Form.Label>
                  Gasto requerido (CLP) <span className="required">*</span>
                </Form.Label>
                <div className="currency-input-wrapper">
                  <span className="currency-symbol">$</span>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.next_level_required_spent}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        next_level_required_spent: e.target.value,
                      })
                    }
                    isInvalid={!!errors.next_level_required_spent}
                    placeholder="50000"
                    className="currency-input"
                  />
                </div>
                <Form.Control.Feedback type="invalid" className={errors.next_level_required_spent ? 'd-block' : ''}>
                  {errors.next_level_required_spent}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Monto que el cliente debe gastar para alcanzar el siguiente nivel
                </Form.Text>
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            type="submit" 
            disabled={loading || !isFormValid()}
            className="btn-submit"
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Creando...
              </>
            ) : (
              'Crear membresía'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateMembership;